import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useRoles } from "@/hooks/useRoles";
import {
  useArchiveUser,
  useUpdateUserAccountLead,
  useUpdateUserName,
  useUpdateUserRole,
} from "@/hooks/useUsers";
import type { User } from "@/interfaces/user";
import type { Role } from "@/lib/roles";

interface UserEditDialogProps {
  user: User | null;
  onClose: () => void;
}

/**
 * Edit box for a user in the admin console. Name/Email/Organization are read-only
 * (no profile-update endpoint yet). The Role field appears whenever the user belongs
 * to a GFG organization (organization.is_gfg) and offers only level-"all" roles — this
 * is where GFG staff roles are set, including the first time. A GFG-org user who has no
 * GFG role yet starts blank. Status can flip Enabled → Archived.
 */
export default function UserEditDialog({ user, onClose }: UserEditDialogProps) {
  const { roles } = useRoles();
  const updateRole = useUpdateUserRole();
  const updateName = useUpdateUserName();
  const updateAccountLead = useUpdateUserAccountLead();
  const archive = useArchiveUser();

  // Org Role (level 1) is editable for everyone; GFG Role (level 2) shows only for users
  // in a GFG organization. Roles aren't ranked — each level is its own editable slot.
  const isGfg = user?.organizationIsGfg === true;
  const orgRoles = roles.filter((r) => r.level === "org");
  const gfgRoles = roles
    .filter((r) => r.level === "all")
    .sort((a, b) => a.label.localeCompare(b.label));

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [orgRole, setOrgRole] = useState<Role | "">("");
  const [gfgRole, setGfgRole] = useState<Role | "">("");
  const [accountLead, setAccountLead] = useState(false);
  const [status, setStatus] = useState<"Enabled" | "Archived">("Enabled");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Seed form state only when a different user is opened.
  // biome-ignore lint/correctness/useExhaustiveDependencies: seed on user change only
  useEffect(() => {
    if (user) {
      setFirstName(user.firstname);
      setLastName(user.lastname);
      setOrgRole(user.orgRole ?? "");
      setGfgRole(user.gfgRole ?? "");
      setAccountLead(user.isAccountLead);
      setStatus(user.archived ? "Archived" : "Enabled");
      setError(null);
    }
  }, [user]);

  async function handleSave() {
    if (!user) return;
    setSaving(true);
    setError(null);
    try {
      const first = firstName.trim();
      const last = lastName.trim();
      if (first && (first !== user.firstname || last !== user.lastname)) {
        await updateName.mutateAsync({ userId: user.id, firstName: first, lastName: last });
      }
      // Org Role (level 1) — assign or clear.
      if (orgRole !== (user.orgRole ?? "")) {
        await updateRole.mutateAsync(
          orgRole
            ? { userId: user.id, role: orgRole }
            : { userId: user.id, role: null, level: "org" },
        );
      }
      // GFG Role (level 2) — GFG-org users only.
      if (isGfg && gfgRole !== (user.gfgRole ?? "")) {
        await updateRole.mutateAsync(
          gfgRole
            ? { userId: user.id, role: gfgRole }
            : { userId: user.id, role: null, level: "all" },
        );
      }
      if (isGfg && accountLead !== user.isAccountLead) {
        await updateAccountLead.mutateAsync({ userId: user.id, isAccountLead: accountLead });
      }
      if (status === "Archived") {
        await archive.mutateAsync(user.id);
      }
      onClose();
    } catch {
      setError("Failed to save changes. You may not have permission for this change.");
    } finally {
      setSaving(false);
    }
  }

  const label = (t: string) => (
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "var(--color-graphite)", mb: 0.5 }}>
      {t}
    </Typography>
  );

  return (
    <Dialog open={!!user} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontFamily: "var(--font-crimson)", fontSize: 24 }}>Edit user</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <TextField
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
              size="small"
            />
          </Box>
          <TextField
            label="Email"
            value={user?.email ?? ""}
            slotProps={{ input: { readOnly: true } }}
            fullWidth
            size="small"
          />
          <TextField
            label="Organization"
            value={user?.organization ?? "—"}
            slotProps={{ input: { readOnly: true } }}
            fullWidth
            size="small"
          />

          <Box>
            {label("Org Role")}
            <Select
              value={orgRole}
              onChange={(e) => setOrgRole(e.target.value as Role | "")}
              fullWidth
              size="small"
            >
              <MenuItem value="" sx={{ color: "var(--color-graphite)" }}>
                None
              </MenuItem>
              {orgRoles.map((r) => (
                <MenuItem key={r.key} value={r.key}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {isGfg && (
            <Box>
              {label("GFG Role")}
              <Select
                value={gfgRole}
                onChange={(e) => setGfgRole(e.target.value as Role | "")}
                fullWidth
                size="small"
              >
                <MenuItem value="" sx={{ color: "var(--color-graphite)" }}>
                  None
                </MenuItem>
                {gfgRoles.map((r) => (
                  <MenuItem key={r.key} value={r.key}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}

          {isGfg && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={accountLead}
                  onChange={(e) => setAccountLead(e.target.checked)}
                  size="small"
                />
              }
              label="Account Lead"
              slotProps={{ typography: { sx: { fontSize: 14 } } }}
            />
          )}

          <Box>
            {label("Status")}
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as "Enabled" | "Archived")}
              fullWidth
              size="small"
            >
              <MenuItem value="Enabled">Enabled</MenuItem>
              <MenuItem value="Archived">Archived</MenuItem>
            </Select>
          </Box>

          {error && <Typography sx={{ fontSize: 13, color: "error.main" }}>{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: "var(--color-graphite)" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          sx={{ bgcolor: "var(--color-rodeo-black)" }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
